import { useContext, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import { AuthContext, ProfileContext } from '../../App';
import { postForm } from '../../util/HttpRequest';
import { formatCurrency } from '../../util/Format';
import Waiting from '../Waiting';
import { RemoteDepositCheckReviewProps } from '../../types/RemoteDepositCheck.types';
import { PostResponse } from '../Models';

function ClaimRemoteDepositCheckReview({ extractedData, onBack, toggleModal, onSubmitResult }: RemoteDepositCheckReviewProps) {
  const [processing, setProcessing] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [submitSuccessMsg, setSubmitSuccessMsg] = useState("");
  const [submitErrorMsg, setSubmitErrorMsg] = useState("");
  const { accessToken, extendSession, user } = useContext(AuthContext);
  const { userProfileLossDraftsClaim } = useContext(ProfileContext);
  const { t } = useTranslation(["Form", "ClaimRemoteDepositCheck"]);
  const [isEditingCheckAmount, setIsEditingCheckAmount] = useState(false);
  const formikRef = useRef<FormikProps<any>>(null);
  const MIN_AMOUNT: number = 0.01;
  const MAX_AMOUNT: number = 9999999.99;

  const checkAmountValidationSchema = Yup.object({
    checkAmount: Yup.string()
      .required(t('ClaimRemoteDepositCheck:lblCheckAmountRequired'))
      .matches(/^\d+\.\d{2}$/, t('ClaimRemoteDepositCheck:lblCheckAmountFormat'))
      .test(
        'min',
        t('ClaimRemoteDepositCheck:lblCheckAmountMin', {
          min: formatCurrency(MIN_AMOUNT)
        }),
        (val) => {
          const num = parseFloat(val || '');
          return !isNaN(num) && num >= MIN_AMOUNT;
        }
      )
      .test(
        'max',
        t('ClaimRemoteDepositCheck:lblCheckAmountMax', {
          max: formatCurrency(MAX_AMOUNT)
        }),
        (val) => {
          const num = parseFloat(val || '');
          return !isNaN(num) && num <= MAX_AMOUNT;
        }
      )
  });

  function renderCheckAmountInputForm(ref: React.RefObject<FormikProps<any> | null>) {
    return (
      <Formik
        innerRef={ref}
        initialValues={{ checkAmount: extractedData?.checkAmount?.toFixed(2) || '0.00' }}
        validationSchema={checkAmountValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmitClaimCheck(values, setSubmitting);
        }}
      >
        {({ values, setFieldValue }) => (
          <Form>
            {isEditingCheckAmount ? (
              <Field name="checkAmount">
                {({ field }: any) => (
                  <Input
                    {...field}
                    prefix="$"
                    placeholder="0.00"
                    style={{ width: 150 }}
                    autoFocus
                    onChange={(e) => {
                      let input = e.target.value;
                      // Allow only numbers and a single decimal
                      if (/^\d*\.?\d{0,2}$/.test(input)) {
                        setFieldValue(field.name, input);
                      }
                    }}
                  />
                )}
              </Field>
            ) : (
              <>
                {formatCurrency(Number(values.checkAmount) || 0)}
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setIsEditingCheckAmount(true);
                  }}
                  disabled={isDisabled}
                />
              </>
            )}
            <br />
            <ErrorMessage name="checkAmount" component="div" className="error" />
          </Form>
        )}
      </Formik>
    );
  }

  function handleSubmitClaimCheck(values, setSubmitting) {
    setProcessing(true);
    setSubmitting(false);   // MUST invoke this before updating any parent props or the form submit happens twice!
    setIsDisabled(true) //disable the submit button while submitting and after submit. user needs to start over the process of they want to restart.
    extendSession();
    console.log('Check Amount:', values.checkAmount);
    const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/RemoteDepositClaimCheck");
    const formData = new FormData();
    formData.append("ClaimId", userProfileLossDraftsClaim.claimId.toString());
    formData.append("UserId", user.id);
    formData.append("CorrectedCheckAmount", values.checkAmount);

    console.log("Submitting Claim Check (Streaming) to " + url);

    postForm(url, accessToken, formData)
      .then((response) => { 
        if (!response.ok) {
          throw new Error(response.status + " - " + response.statusText)
        }
        return response.json() as Promise<PostResponse>;
        
      })
      .then((response) => {
        setIsEditingCheckAmount(false); // always close the check amount editing UI
        if(response.success)
        {
          onSubmitResult('success'); //notify parent about the success
          setSubmitSuccessMsg(
            `${t('ClaimRemoteDepositCheck:checkDepositSuccessTitle')} ${t('ClaimRemoteDepositCheck:checkDepositSuccessBody')}`
          );
        }
        else //success = false
        { 
          if(response.message === 'dupe') //if it is a duplicate check we don't want the users to retry so we need to show a different message
          {
            onSubmitResult('failed'); //notify parent about the failure
            setSubmitErrorMsg(t('ClaimRemoteDepositCheck:checkDepositFailureDuplicate'));
          }
          else{
            onSubmitResult('failed'); //notify parent about the failure
            setSubmitErrorMsg(t('ClaimRemoteDepositCheck:checkDepositFailure'));
          }
        }
      })
      .catch((err) => {
        onSubmitResult('failed'); //notify parent about the failure
        setSubmitErrorMsg(t('ClaimRemoteDepositCheck:checkDepositFailure'));
        console.log("Error occuring while calling handleSubmitCheckRequest(): ", err.message);
      })
      .finally(() => {
        setProcessing(false);
      })
  };

  function renderForm() {
    return (
      <>
        {!extractedData?.message ? (
          <>
            <span className="preline-text">
              {t('ClaimRemoteDepositCheck:checkReviewSubmitInstructions')}
            </span>
            <div className="row-container">
              <br /><br />
              <div className="column-container">
                <div className="key" >{t('ClaimRemoteDepositCheck:lblCheckNumber')}</div>
                <div className="value">
                  {extractedData?.checkNumber}
                </div>
              </div>
              <div className="column-container">
                <div className="key even">{t('ClaimRemoteDepositCheck:lblCheckRoutingNumber')}</div>
                <div className="value even">
                  {extractedData?.routingNumber}
                </div>
              </div>
              <div className="column-container">
                <div className="key">{t('ClaimRemoteDepositCheck:lblCheckAccountNumber')}</div>
                <div className="value">
                  {extractedData?.accountNumber}
                </div>
              </div>
              <div className="column-container">
                <div className="key even">{t('ClaimRemoteDepositCheck:lblCheckAmount')}</div>
                <div className="value even">
                  {renderCheckAmountInputForm(formikRef)}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
             {/* 
               Attempt to translate a specific error message based on a code (e.g., "MICR Intrusion Detection") 
               returned from the backend and stored in extractedData.message.

               - `ns`: Specifies the namespace ("ClaimRemoteDepositCheck") to search for translation keys. 
               This is required because you're using multiple namespaces in `useTranslation([...])`,
               and the key you're looking up does not exist in the default one ("Form"). The first namespace 
               in the array — in this case, "Form" — is treated as the default namespace for all t() calls 
               unless we explicitly specify ns. we can use ClaimRemoteDepositCheck.IQAMessage.${extractedData.message},
               but only if we are using the default namespace — and in our case,our default namespace is "Form".

              - `defaultValue`: If the above translation key doesn't exist, fall back to "IQAMessage.Default"
               within the same namespace.
            */}
              <h4>
                {t(`IQAMessage.${extractedData.message}`, {
                  ns: "ClaimRemoteDepositCheck",
                  defaultValue: t("IQAMessage.Default", {
                    ns: "ClaimRemoteDepositCheck"
                  })
                })}
              </h4>
             <br /><br /> 
             {/* show a static message to users if the check keeps failing extraction */}
              <p><i>{t('ClaimRemoteDepositCheck:checkValidationFailureWarning')}</i></p>
          </>
        )}
        <br /><br />
        {!!submitSuccessMsg ? (
          <Button type="default" className="button" onClick={() => toggleModal(false)}>
            {t('Form:btnClose')}
          </Button>
        ) : (
          <Button type="default" className="button" disabled={processing} onClick={onBack} >
            {t('Form:btnStartOver')}
          </Button>
        )}
        &nbsp;&nbsp;
        <Button type="primary" className="button submit" disabled={!!extractedData.message || isDisabled} onClick={() => formikRef.current?.submitForm()}>
          {t('Form:btnSubmit')} 
        </Button>
        <br /><br />
        {
          processing && (<span>{t('ClaimRemoteDepositCheck:lblProcessingSubmit')} &nbsp;<Waiting /></span>)
        }
        {submitSuccessMsg && <div className="success">{submitSuccessMsg}</div>}
        {submitErrorMsg && <div className="error">{submitErrorMsg}</div>}
      </>
    );
  }

  return (
    <> {renderForm()} </>
  );
};
export default ClaimRemoteDepositCheckReview;